export interface TextHighlight { start: number; end: number; label: string }
interface Node {
  type: string; value?: string; children?: Node[]
  position?: { start: { offset?: number }; end: { offset?: number } }
  data?: { hName?: string; hProperties?: Record<string, unknown> }
}

/** Add marks to parsed Markdown; never insert HTML or alter the source text. */
export function markdownHighlights({ source, highlights }: { source: string; highlights: TextHighlight[] }) {
  function split(value: string, offset: number): Node[] {
    const nodes: Node[] = []
    let cursor = 0
    for (const range of highlights) {
      const start = Math.max(0, range.start - offset), end = Math.min(value.length, range.end - offset)
      if (end <= start) continue
      if (start > cursor) nodes.push({ type: 'text', value: value.slice(cursor, start) })
      nodes.push({ type: 'emphasis', data: { hName: 'mark', hProperties: { title: range.label } },
        children: [{ type: 'text', value: value.slice(start, end) }] })
      cursor = end
    }
    if (cursor < value.length) nodes.push({ type: 'text', value: value.slice(cursor) })
    return nodes
  }
  function visit(node: Node): Node[] {
    const start = node.position?.start.offset, end = node.position?.end.offset
    if ((node.type === 'text' || node.type === 'inlineCode') && node.value !== undefined && start !== undefined && end !== undefined) {
      const raw = source.slice(start, end)
      if (node.type === 'text' && raw === node.value) return split(node.value, start)
      if (node.type === 'inlineCode') {
        const delimiter = raw.match(/^`+/)?.[0].length ?? 0
        if (delimiter && raw.slice(delimiter, -delimiter) === node.value) {
          return [{ type: 'emphasis', data: { hName: 'code' }, children: split(node.value, start + delimiter) }]
        }
      }
      // Escapes and entities can change character offsets. Mark only a fully covered node.
      const range = highlights.find(range => range.start <= start && range.end >= end)
      if (range) return [{ type: 'emphasis', data: { hName: 'mark', hProperties: { title: range.label } }, children: [node] }]
    }
    if (node.children) node.children = node.children.flatMap(visit)
    return [node]
  }
  return (tree: Node) => { if (highlights.length && tree.children) tree.children = tree.children.flatMap(visit) }
}
